/**
 * Micheals Infrastructure Advisory - PRINCIPAL SALES ENGINE HOOK (HARDENED)
 * ROLE: Principal Telemetry Ingress & Strategic Tier Dispatch
 * VERSION: 3.3.9 (Build 2026.07.07 - Integration Verified)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { secureFetch, API_BASE, ROUTES } from "../utils/apiClient";

export const useSalesEngine = (passedUrl = '') => {
    // Standardize URL usage
    const engineUrl = passedUrl || API_BASE;

    const [isSyncing, setIsSyncing] = useState(false);
    const [lastScore, setLastScore] = useState(0);
    const abortControllerRef = useRef(null);

    const fireRequest = useCallback(async (endpoint, payload, signal = null) => {
        const SESSION_ID = window.AVA_SESSION_ID || localStorage.getItem('session_id');

        if (!SESSION_ID || ['undefined', 'null', ''].includes(String(SESSION_ID))) {
            return null;
        }

        // Use engineUrl instead of global API_BASE for flexibility
        const targetUrl = `${engineUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

        try {
            const response = await secureFetch(targetUrl, {
                method: 'POST',
                signal,
                headers: {
                    'Content-Type': 'application/json',
                    'x-ava-session-id': SESSION_ID
                },
                body: JSON.stringify({
                    ...payload,
                    sessionId: SESSION_ID,
                    timestamp: new Date().toISOString()
                })
            });

            if (response && typeof response.json === 'function') {
                if (!response.ok) {
                    console.warn(`Gateway Error: Status ${response.status}`);
                    return null;
                }
                return await response.json();
            }

            return response;
        } catch (err) {
            if (err.name === 'AbortError') return null;
            console.error("❌ [SALES_ENGINE]: FireRequest failed:", err);
            throw err;
        }
    }, [engineUrl]);

    const selectConsultationTier = useCallback(async (tier) => {
        setIsSyncing(true);
        try {
            return await fireRequest(ROUTES.CONSULTATION, { selectedTier: tier });
        } catch (err) {
            console.error("❌ [MED_DISPATCH_FAILED]:", err.message);
            return null;
        } finally {
            setIsSyncing(false);
        }
    }, [fireRequest]);

    const syncLeadData = useCallback(async (data) => {
        if (!data) return null;
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        setIsSyncing(true);
        try {
            const result = await fireRequest(ROUTES.PROXY_TRACK, data, abortControllerRef.current.signal);
            
            if (result?.success) setLastScore(result.score || 0);
            return result;
        } catch (err) {
            return null; 
        } finally {
            setIsSyncing(false);
        }
    }, [fireRequest]);

    useEffect(() => {
        return () => { 
            if (abortControllerRef.current) abortControllerRef.current.abort(); 
        };
    }, []);

    return { 
        syncLeadData, 
        selectConsultationTier, 
        isSyncing, 
        lastScore, 
        status: isSyncing ? 'SYNCING' : 'STABLE' 
    };
};